/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable max-lines */
/* eslint-disable max-statements */
import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { Group as GroupType } from 'konva/lib/Group';
import { Stage as StageType } from 'konva/lib/Stage';
import { transparentize } from 'polished';
import styled from 'styled-components';

import { getRoadmapPermissionList } from '@/api/shared/roadmap';
import routes from '@/constants/routes';
import {
  getAllRoadmapNodes,
  getFiltersByRoadmapId,
  getRoadmapsDropdownVisible,
  getRoadmapSections,
  getRoadmapsPopupVisible,
  getRoadmapsRoadmapVisible,
  getRoadmapTimePeriods,
  updateNodesSectionAndTimePeriod,
  updateSection,
} from '@/features/canvas/api';
import {
  BudgetDropdownVisible,
  getBudgetDropdownsVisible,
} from '@/features/canvas/api/budget-dropdown-visible';
import {
  BudgetPopupVisible,
  getBudgetPopupsVisible,
} from '@/features/canvas/api/budget-popup-visible';
import {
  BudgetRoadmapVisible,
  getBudgetRoadmapVisible,
} from '@/features/canvas/api/budget-roadmap-visible';
import { getAllBudgetsForRoadmap } from '@/features/canvas/api/initiative_budgets';
import {
  HORIZONTAL_OFFSET,
  SIDEBAR_ACCORDION_KEY_MAP,
  STAGE_OFFSET,
  VERTICAL_OFFSET,
} from '@/features/canvas/constants';
import { InitiativeBudgetData } from '@/features/canvas/types/budget-type';
import { FilterWithSortOrder } from '@/features/canvas/types/filter';
import { Point } from '@/features/canvas/types/point';
import { HorizontalSection, type VerticalSection } from '@/features/canvas/types/section';
import { generateTemporaryNumberId } from '@/features/canvas/utils/generate-temporary-number-id';
import { isValidNodeSize } from '@/features/canvas/utils/get-node-radius';
import { getRoadmapById } from '@/features/roadmaps/api';
import { components } from '@/types/api';

import { calculateAbsolutePositionOnCanvasFromPercentageToPx } from '../utils/calculate-absolute-position-on-canvas-in-px';
import { getIntersectionSectionAndTimePeriodId } from '../utils/get-intersection-section-and-time-period-id';

export type ViewMode = 'filter' | 'budget';
export type BudgetViewTab = 'total' | 'capex-opex' | 'spend';
export type CanvasViewType = 'canvas' | 'swimlane' | 'gantt';

export type Node = components['schemas']['Node'];

export interface FilteredNodes extends Node {
  color: string;
  shape: 'circle' | 'square' | 'triangle';
  size?: number;
}

type ReorderedOptionsMap = Record<number, components['schemas']['FilterOption'][]>;

const EditorContext = createContext({
  roadmap: {} as components['schemas']['RoadmapDetail'],
  filters: {} as components['schemas']['Filter'][],
  filteredNodes: [] as FilteredNodes[],
  isDraggingTab: false,
  setIsDraggingTab: (() => {}) as Dispatch<SetStateAction<boolean>>,
  draggedTabPositionChange: 0,
  filterNodes: (() => {}) as ({
    filter,
    optionColor,
    optionSize,
    optionId,
    multiFilterColor,
  }: {
    filter: components['schemas']['Filter'];
    optionColor: string;
    optionSize: number;
    multiFilterColor?: string;
    optionId?: number;
  }) => void,
  setDraggedTabPositionChange: (() => {}) as Dispatch<SetStateAction<number>>,
  setIsSidebarContentLoading: (() => {}) as Dispatch<SetStateAction<boolean>>,
  isSidebarContentLoading: false as boolean,
  setSelectedNodeId: (() => {}) as Dispatch<SetStateAction<number | null>>,
  setSelectedInitiativeId: (() => {}) as Dispatch<SetStateAction<number | null>>,
  selectedNodeId: null as number | null,
  selectedInitiativeId: null as number | null,
  editingInitiativeId: null as number | null,
  setEditingInitiativeId: (() => {}) as Dispatch<SetStateAction<number | null>>,
  isCreatingNewInitiative: false,
  setIsCreatingNewInitiative: (() => {}) as Dispatch<SetStateAction<boolean>>,
  sortedTimePeriods: [] as components['schemas']['TimePeriod'][],
  sortedVerticalSections: [] as VerticalSection[],
  sortedHorizontalSections: [] as HorizontalSection[],
  roadmapNodes: [] as components['schemas']['Node'][],
  setFilteredNodes: (() => {}) as Dispatch<SetStateAction<FilteredNodes[] | []>>,
  activeFilterIds: [] as number[],
  setActiveFilterIds: (() => {}) as Dispatch<SetStateAction<number[]>>,
  isPreview: false,
  setIsPreview: (() => {}) as Dispatch<SetStateAction<boolean>>,
  roadmapVisible: [] as components['schemas']['RoadmapVisible'][],
  popupVisible: [] as components['schemas']['PopupVisible'][],
  budgetPopupVisible: [] as BudgetPopupVisible[],
  budgetDropdownVisible: [] as BudgetDropdownVisible[],
  budgetRoadmapVisible: [] as BudgetRoadmapVisible[],
  dropdownVisible: [] as components['schemas']['DropdownVisible'][],
  optionCheckedIds: [] as number[],
  setOptionCheckedIds: (() => {}) as Dispatch<SetStateAction<number[]>>,
  isPublicView: false,
  isInitiativeDetailDialogOpen: false,
  setIsInitiativeDetailDialogOpen: (() => {}) as Dispatch<SetStateAction<boolean>>,
  expandedSidebarAccordionKey: null as keyof typeof SIDEBAR_ACCORDION_KEY_MAP | null,
  setExpandedSidebarAccordionKey: (() => {}) as Dispatch<
    SetStateAction<keyof typeof SIDEBAR_ACCORDION_KEY_MAP | null>
  >,
  generateUniqueIntTemporaryId: (() => {}) as () => number,
  temporaryCreatedIds: new Set<number>(),
  isSwimlaneView: false,
  setIsSwimlaneView: (() => {}) as Dispatch<SetStateAction<boolean>>,
  isGanttView: false,
  setIsGanttView: (() => {}) as Dispatch<SetStateAction<boolean>>,
  setShowDependencies: (() => {}) as Dispatch<SetStateAction<boolean>>,
  showDependencies: false,
  isMultiFilterEnabled: false,
  setIsMultiFilterEnabled: (() => {}) as Dispatch<SetStateAction<boolean>>,
  stageRef: null as unknown as MutableRefObject<StageType | null>,
  canvasAreaRef: null as unknown as MutableRefObject<GroupType | null>,
  canvasAreaOffset: { x: 0, y: 0 } as Point,
  height: 0 as number | undefined,
  width: 0 as number | undefined,
  setHeight: (() => {}) as Dispatch<SetStateAction<number | undefined>>,
  setWidth: (() => {}) as Dispatch<SetStateAction<number | undefined>>,
  widthToFitContent: 0,
  heightToFitContent: 0,
  detectNodeTimePeriodAndSectionIdChanges: (() => {}) as () => void,
  setShouldRecalculateNodePositions: (() => {}) as Dispatch<SetStateAction<boolean>>,
  isSelectingDependencies: false,
  setIsSelectingDependencies: (() => {}) as Dispatch<SetStateAction<boolean>>,
  sortedTimePeriodsRef: null as unknown as MutableRefObject<components['schemas']['TimePeriod'][]>,
  sortedVerticalSectionsRef: null as unknown as MutableRefObject<VerticalSection[]>,
  sortedHorizontalSectionsRef: null as unknown as MutableRefObject<HorizontalSection[]>,
  filteredNodesRef: null as unknown as MutableRefObject<FilteredNodes[]>,
  lastSavedTime: new Date(),
  updateLastSavedTime: (() => {}) as () => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  roadmapPermissions: [] as unknown[] as any[],
  reorderedFilterOptions: {} as ReorderedOptionsMap,
  setReorderedFilterOptions: (() => {}) as Dispatch<SetStateAction<ReorderedOptionsMap>>,
  sortableFilters: [] as FilterWithSortOrder[],
  setSortableFilters: (() => {}) as Dispatch<SetStateAction<FilterWithSortOrder[]>>,
  activeDependencyNodeIds: new Set<number>(),
  setActiveDependencyNodeIds: (() => {}) as Dispatch<SetStateAction<Set<number>>>,
  activeDependencyConnectionIds: new Set<string>(),
  setActiveDependencyConnectionIds: (() => {}) as Dispatch<SetStateAction<Set<string>>>,
  activeDisabledConnectionIds: new Set<string>(),
  setActiveDisabledConnectionIds: (() => {}) as Dispatch<SetStateAction<Set<string>>>,
  viewMode: 'filter' as ViewMode,
  setViewMode: (() => {}) as Dispatch<SetStateAction<ViewMode>>,
  budgetViewTab: 'total' as BudgetViewTab,
  setBudgetViewTab: (() => {}) as Dispatch<SetStateAction<BudgetViewTab>>,
  initiativeBudgets: new Map<number, InitiativeBudgetData>(),
  setInitiativeBudgets: (() => {}) as Dispatch<SetStateAction<Map<number, InitiativeBudgetData>>>,
  isLoadingBudgets: false,
  setIsLoadingBudgets: (() => {}) as Dispatch<SetStateAction<boolean>>,
  allBudgets: [] as InitiativeBudgetData[],
  toggledBudgetNodeIds: new Set<number>(),
  setToggledBudgetNodeIds: (() => {}) as Dispatch<SetStateAction<Set<number>>>,
  toggleBudgetNode: (() => {}) as (nodeId: number) => void,
  hiddenChildNodeIds: new Set<number>(),
});

export const EditorContextProvider = ({
  children,
  isPublicView,
  roadmapId: id,
  defaultFilteredNodes,
}: {
  children: ReactNode;
  defaultFilteredNodes?: Node[];
  isPublicView?: boolean;
  roadmapId?: number | null;
}) => {
  const stageRef = useRef<StageType | null>(null);
  const canvasAreaRef = useRef<GroupType | null>(null);

  const prevTimePeriodsWidthRef = useRef<number[]>([]);
  const prevTimePeriodsHeightRef = useRef<number[]>([]);
  const prevSectionsWidthRef = useRef<number[]>([]);

  const navigate = useNavigate();

  const { id: roadmapIdParam } = useParams<{ id: string }>();

  const [isDraggingTab, setIsDraggingTab] = useState(false);
  const [draggedTabPositionChange, setDraggedTabPositionChange] = useState(0);
  const [filteredNodes, setFilteredNodes] = useState<FilteredNodes[] | []>(
    (defaultFilteredNodes as FilteredNodes[]) || []
  );

  const [width, setWidth] = useState<number | undefined>();
  const [height, setHeight] = useState<number | undefined>();

  const [isSidebarContentLoading, setIsSidebarContentLoading] = useState<boolean>(false);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [selectedInitiativeId, setSelectedInitiativeId] = useState<number | null>(null);
  const [editingInitiativeId, setEditingInitiativeId] = useState<number | null>(null);
  const [isCreatingNewInitiative, setIsCreatingNewInitiative] = useState(false);
  const [activeFilterIds, setActiveFilterIds] = useState<number[]>([]);
  const [optionCheckedIds, setOptionCheckedIds] = useState<number[]>([]);
  const [isPreview, setIsPreview] = useState<boolean>(Boolean(isPublicView));
  const [isInitiativeDetailDialogOpen, setIsInitiativeDetailDialogOpen] = useState<boolean>(false);
  const [expandedSidebarAccordionKey, setExpandedSidebarAccordionKey] = useState<
    keyof typeof SIDEBAR_ACCORDION_KEY_MAP | null
  >(null);
  const [isSwimlaneView, setIsSwimlaneView] = useState<boolean>(false);
  const [isGanttView, setIsGanttView] = useState<boolean>(false);
  const [showDependencies, setShowDependencies] = useState<boolean>(false);
  const [isMultiFilterEnabled, setIsMultiFilterEnabled] = useState<boolean>(false);
  const [isSelectingDependencies, setIsSelectingDependencies] = useState<boolean>(false);
  const [shouldRecalculateNodePositions, setShouldRecalculateNodePositions] =
    useState<boolean>(false);

  const [lastSavedTime, setLastSavedTime] = useState<Date>(new Date());

  const [reorderedFilterOptions, setReorderedFilterOptions] = useState<ReorderedOptionsMap>({});
  const [sortableFilters, setSortableFilters] = useState<FilterWithSortOrder[]>([]);

  const [activeDependencyNodeIds, setActiveDependencyNodeIds] = useState<Set<number>>(new Set());

  const [activeDependencyConnectionIds, setActiveDependencyConnectionIds] = useState<Set<string>>(
    new Set()
  );

  const [activeDisabledConnectionIds, setActiveDisabledConnectionIds] = useState<Set<string>>(
    new Set()
  );

  const [viewMode, setViewMode] = useState<ViewMode>('filter');
  const [budgetViewTab, setBudgetViewTab] = useState<BudgetViewTab>('total');
  const [initiativeBudgets, setInitiativeBudgets] = useState<Map<number, InitiativeBudgetData>>(
    new Map()
  );
  const [isLoadingBudgets, setIsLoadingBudgets] = useState<boolean>(false);

  // Add toggled budget nodes state
  const [toggledBudgetNodeIds, setToggledBudgetNodeIds] = useState<Set<number>>(new Set());

  // Add hidden child nodes state
  const [hiddenChildNodeIds, setHiddenChildNodeIds] = useState<Set<number>>(new Set());

  const widthToFitContent = (width || 0) - HORIZONTAL_OFFSET - 2 * STAGE_OFFSET;
  const heightToFitContent = (height || 0) - 2 * VERTICAL_OFFSET - 2 * STAGE_OFFSET;

  const canvasAreaOffset = canvasAreaRef.current?.getAbsolutePosition() || {
    x: STAGE_OFFSET + HORIZONTAL_OFFSET,
    y: STAGE_OFFSET + VERTICAL_OFFSET,
  };

  const roadmapId = id || Number(roadmapIdParam);

  // Add toggle function
  const toggleBudgetNode = useCallback((nodeId: number) => {
    setToggledBudgetNodeIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Clear toggled nodes when switching views
  useEffect(() => {
    if (viewMode !== 'budget') {
      setToggledBudgetNodeIds(new Set());
    }
  }, [viewMode]);

  // Monitor filteredNodes changes
  useEffect(() => {}, [filteredNodes]);

  const { data: roadmap, isLoading: isRoadmapLoading } = useQuery(
    [getRoadmapById.name, Number(roadmapId)],
    async () => {
      const data = await getRoadmapById(Number(roadmapId));
      return data;
    },
    {
      enabled: Boolean(roadmapId) && !isPublicView,
    }
  );

  const { data: roadmapPermissions = [] } = useQuery(
    [getRoadmapPermissionList.name, roadmapId],
    () => getRoadmapPermissionList({ roadmapId: Number(roadmapId) }),
    { enabled: Boolean(roadmapId && !isPublicView) }
  );

  // Update filters query:
  const { data: filters } = useQuery<components['schemas']['Filter'][]>(
    [getFiltersByRoadmapId.name, Number(roadmapId)],
    () => {
      return getFiltersByRoadmapId(roadmap!.id!);
    },
    {
      enabled: Boolean(roadmap?.id) && !isPublicView, // Add !isPublicView check
    }
  );

  // Update roadmapNodes query:
  const { data: roadmapNodes = [], isLoading: areRoadmapNodesLoading } = useQuery(
    [getAllRoadmapNodes.name, Number(roadmapId)],
    () => getAllRoadmapNodes({ roadmapId: Number(roadmapId) }),
    {
      enabled: Boolean(roadmapId) && !isPublicView, // Add !isPublicView check
      cacheTime: 0,
      onSuccess: data => {
        if (isPublicView) {
          // In public view, no nodes are hidden here (handled by defaultFilteredNodes)
          setHiddenChildNodeIds(new Set());
        } else {
          // Filter out child initiatives that have a parent in the same roadmap (KON-145)
          const nodesData = data as FilteredNodes[];

          // Create a set of initiative IDs that exist in this roadmap
          const roadmapInitiativeIds = new Set(
            nodesData.filter(node => node.initiatives?.id).map(node => node.initiatives!.id)
          );

          // Track which nodes are hidden and filter them out
          const hiddenNodeIds = new Set<number>();
          const filteredData = nodesData.filter(node => {
            // If no initiative, keep the node
            if (!node.initiatives?.id) return true;

            // If initiative has no parents, keep the node
            const initiative = node.initiatives as components['schemas']['Initiative'] & {
              parents?: { id: number; title: string }[];
            };
            const { parents } = initiative;

            if (!parents || !Array.isArray(parents) || parents.length === 0) return true;

            // Check if any parent exists in the same roadmap
            const hasParentInSameRoadmap = parents.some((parent: { id: number }) =>
              roadmapInitiativeIds.has(parent.id)
            );

            // If node should be hidden, add to hidden set and filter out
            if (hasParentInSameRoadmap && node.id) {
              hiddenNodeIds.add(node.id);
              return false;
            }

            return true;
          });

          setHiddenChildNodeIds(hiddenNodeIds);
          setFilteredNodes(filteredData);
        }
      },
      refetchOnWindowFocus: false,
    }
  );

  // Update timePeriods query:
  const { data: timePeriods = [], isLoading: areTimePeriodsLoading } = useQuery(
    [getRoadmapTimePeriods.name, Number(roadmapId)],
    () => getRoadmapTimePeriods({ roadmapId: Number(roadmapId) }),
    {
      enabled: Boolean(roadmapId) && !isPublicView, // Add !isPublicView check
    }
  );

  // Update sections query:
  const { data: sections = [], isLoading: areSectionsLoading } = useQuery(
    [getRoadmapSections.name, Number(roadmapId)],
    () => getRoadmapSections({ roadmapId: Number(roadmapId) }),
    {
      enabled: Boolean(roadmapId) && !isPublicView, // Add !isPublicView check
    }
  );

  // Update roadmapVisible query:
  const { data: roadmapVisible = [], isLoading: isLoadingRoadmapVisible } = useQuery(
    [getRoadmapsRoadmapVisible.name, Number(roadmapId)],
    () => getRoadmapsRoadmapVisible({ roadmapId: Number(roadmapId) }),
    { enabled: Boolean(roadmapId) && !isPublicView } // Add !isPublicView check
  );

  // Update popupVisible query:
  const { data: popupVisible = [], isLoading: isLoadingPopupVisible } = useQuery(
    [getRoadmapsPopupVisible.name, Number(roadmapId)],
    () => getRoadmapsPopupVisible({ roadmapId: Number(roadmapId) }),
    {
      enabled: Boolean(roadmapId) && !isPublicView,
      staleTime: 0, // Always fetch fresh data
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: true, // Refetch when window regains focus
    }
  );

  // Update budgetPopupVisible query:
  const { data: budgetPopupVisible = [] } = useQuery(
    [getBudgetPopupsVisible.name, Number(roadmapId)],
    () => {
      return getBudgetPopupsVisible({ roadmapId: Number(roadmapId) });
    },
    {
      enabled: Boolean(roadmapId) && !isPublicView,
      staleTime: 0, // Always fetch fresh data
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: true, // Refetch when window regains focus
    }
  );

  // Update budgetDropdownVisible query:
  const { data: budgetDropdownVisible = [] } = useQuery(
    [getBudgetDropdownsVisible.name, Number(roadmapId)],
    () => getBudgetDropdownsVisible({ roadmapId: Number(roadmapId) }),
    { enabled: Boolean(roadmapId) && !isPublicView }
  );

  // Update budgetRoadmapVisible query:
  const budgetRoadmapVisibleQueryKey = [getBudgetRoadmapVisible.name, Number(roadmapId)];

  const { data: budgetRoadmapVisible = [] } = useQuery(
    budgetRoadmapVisibleQueryKey,
    () => getBudgetRoadmapVisible({ roadmapId: Number(roadmapId) }),
    {
      enabled: Boolean(roadmapId), // Remove !isPublicView so it works in public view
      staleTime: isPublicView ? Infinity : 0, // In public view, use cached data forever
    }
  );

  // Update dropdownVisible query:
  const { data: dropdownVisible = [], isLoading: isLoadingDropdownVisible } = useQuery(
    [getRoadmapsDropdownVisible.name, Number(roadmapId)],
    () => getRoadmapsDropdownVisible({ roadmapId: Number(roadmapId) }),
    { enabled: Boolean(roadmapId) && !isPublicView } // Add !isPublicView check
  );

  // Update allBudgets query:
  const { data: allBudgets = [], isLoading: areBudgetsLoading } = useQuery(
    [getAllBudgetsForRoadmap.name, roadmapId],
    () => getAllBudgetsForRoadmap({ roadmapId: Number(roadmapId) }),
    {
      enabled: Boolean(roadmapId) && !isPublicView,
      staleTime: 0, // Always fetch fresh data
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: true, // Refetch when window regains focus
    }
  );

  const sortedTimePeriods = useMemo(
    () =>
      [...timePeriods].sort(
        ({ order: firstTimePeriodOrder }, { order: secondTimePeriodOrder }) =>
          firstTimePeriodOrder - secondTimePeriodOrder
      ),
    [timePeriods]
  );

  const { mutateAsync: updateSectionMutation } = useMutation(
    ({ sectionData }: { sectionData: components['schemas']['Section'] }) =>
      updateSection({ sectionId: Number(sectionData.id), newSectionData: sectionData }),
    {}
  );

  // checks if two sorted sections have the same order attribute value
  const isResortingNeeded = (sortedSections: VerticalSection[] | HorizontalSection[]) => {
    return sortedSections.some(({ order }, index) => {
      const nextIndex = index + 1;

      if (sortedSections[nextIndex]) {
        return sortedSections[nextIndex].order === order;
      }

      return false;
    });
  };

  // eslint-disable-next-line no-warning-comments
  // TODO fix return type
  // This fixes bug when there were two sections with the same order attribute value,
  // which then reordered sections after mutation.
  const resortSections = useCallback(
    (sortedSections: VerticalSection[] | HorizontalSection[]) => {
      const fixedSortedSections = sortedSections.map((section, index) => ({
        ...section,
        order: index + 1,
      }));

      // update the order in DB as well
      fixedSortedSections.map(section => updateSectionMutation({ sectionData: section }));

      return fixedSortedSections;
    },
    [updateSectionMutation]
  );

  const sortedVerticalSections = useMemo(() => {
    let sortedSections = (
      sections.filter(({ orientation }) => orientation === 'vertical') as VerticalSection[]
    ).sort(
      ({ order: firstTimePeriodOrder }, { order: secondTimePeriodOrder }) =>
        firstTimePeriodOrder - secondTimePeriodOrder
    );

    if (isResortingNeeded(sortedSections)) {
      sortedSections = resortSections(sortedSections) as VerticalSection[];
    }

    return sortedSections;
  }, [resortSections, sections]);

  const sortedHorizontalSections = useMemo(() => {
    let sortedSections = (
      sections.filter(({ orientation }) => orientation === 'horizontal') as HorizontalSection[]
    ).sort(
      ({ order: firstTimePeriodOrder }, { order: secondTimePeriodOrder }) =>
        firstTimePeriodOrder - secondTimePeriodOrder
    );

    if (isResortingNeeded(sortedSections)) {
      sortedSections = resortSections(sortedSections) as HorizontalSection[];
    }

    return sortedSections;
  }, [resortSections, sections]);

  const sortedTimePeriodsRef = useRef<components['schemas']['TimePeriod'][]>(sortedTimePeriods);
  const sortedVerticalSectionsRef = useRef<VerticalSection[]>(sortedVerticalSections);
  const sortedHorizontalSectionsRef = useRef<HorizontalSection[]>(sortedHorizontalSections);
  const filteredNodesRef = useRef<FilteredNodes[]>(filteredNodes);

  const temporaryCreatedIds = useMemo(() => new Set<number>(), []);

  const generateUniqueIntTemporaryId = useCallback((): number => {
    const newId = generateTemporaryNumberId({ createdIds: temporaryCreatedIds });
    temporaryCreatedIds.add(newId);
    return newId;
  }, [temporaryCreatedIds]);

  const { mutateAsync: updateRoadmapNodesSectionAndTimePeriod } = useMutation(
    ({ nodes, roadmapIds }: { nodes: components['schemas']['NodeUpdate'][]; roadmapIds: number }) =>
      updateNodesSectionAndTimePeriod({ roadmapId: roadmapIds, nodes }),
    { onSuccess: () => updateLastSavedTime() }
  );

  const detectNodeTimePeriodAndSectionIdChanges = useCallback(() => {
    const nodesToUpdate: components['schemas']['NodeUpdate'][] = [];

    filteredNodes.forEach(node => {
      const nodePositionRelativeToCanvasInPx = calculateAbsolutePositionOnCanvasFromPercentageToPx({
        height: heightToFitContent,
        width: widthToFitContent,
        xPercentage: node.x,
        yPercentage: node.y,
        offsetX: canvasAreaOffset.x,
        offsetY: canvasAreaOffset.y,
      });

      const intersections = stageRef.current?.getAllIntersections(nodePositionRelativeToCanvasInPx);

      if (!intersections) {
        return;
      }

      const { sectionId, timePeriodId } = getIntersectionSectionAndTimePeriodId(intersections);

      const shouldUpdateNode =
        node.sectionId !== Number(sectionId) || node.timePeriodId !== Number(timePeriodId);

      if (shouldUpdateNode) {
        nodesToUpdate.push({
          nodeId: node.id,
          timePeriodId: Number(timePeriodId),
          sectionId: Number(sectionId),
        });
      }
    });
    return nodesToUpdate;
  }, [
    canvasAreaOffset.x,
    canvasAreaOffset.y,
    filteredNodes,
    heightToFitContent,
    widthToFitContent,
  ]);

  const isLoading = isPublicView
    ? false // In public view, we don't wait for queries since they're disabled
    : isRoadmapLoading ||
      areRoadmapNodesLoading ||
      areTimePeriodsLoading ||
      areSectionsLoading ||
      isLoadingRoadmapVisible ||
      isLoadingPopupVisible ||
      isLoadingDropdownVisible ||
      areBudgetsLoading;

  // Apply KON-145 child-hiding filter to defaultFilteredNodes for public view
  useEffect(() => {
    if (isPublicView && defaultFilteredNodes) {
      // Apply the same KON-145 filtering logic as in getAllRoadmapNodes onSuccess handler
      const nodesData = defaultFilteredNodes as FilteredNodes[];

      // Create a set of initiative IDs that exist in this roadmap
      const roadmapInitiativeIds = new Set(
        nodesData.filter(node => node.initiatives?.id).map(node => node.initiatives!.id)
      );

      // Track which nodes are hidden and filter them out
      const hiddenNodeIds = new Set<number>();
      const filteredData = nodesData.filter(node => {
        // If no initiative, keep the node
        if (!node.initiatives?.id) return true;

        // If initiative has no parents, keep the node
        const initiative = node.initiatives as components['schemas']['Initiative'] & {
          parents?: { id: number; title: string }[];
        };
        const { parents } = initiative;

        if (!parents || !Array.isArray(parents) || parents.length === 0) return true;

        // Check if any parent exists in the same roadmap
        const hasParentInSameRoadmap = parents.some((parent: { id: number }) =>
          roadmapInitiativeIds.has(parent.id)
        );

        // If node should be hidden, add to hidden set and filter out
        if (hasParentInSameRoadmap && node.id) {
          hiddenNodeIds.add(node.id);
          return false;
        }

        return true;
      });

      setHiddenChildNodeIds(hiddenNodeIds);
      setFilteredNodes(filteredData);
    }
  }, [isPublicView, defaultFilteredNodes]);

  const updateNodes = useCallback(
    async (nodes: components['schemas']['NodeUpdate'][]) => {
      const updatedNodes = await updateRoadmapNodesSectionAndTimePeriod({
        roadmapIds: roadmapId,
        nodes,
      });

      const updatedNodeIds = updatedNodes.map(un => un.nodeId);

      const newFilteredNodes = filteredNodes.map(node => {
        if (updatedNodeIds.includes(node.id)) {
          const updatedNode = updatedNodes.find(un => un.nodeId === node.id);
          return {
            ...node,
            sectionId: updatedNode?.sectionId || node.sectionId,
            timePeriodId: updatedNode?.timePeriodId || node.timePeriodId,
          };
        }
        return node;
      });

      setFilteredNodes(newFilteredNodes);
    },
    [filteredNodes, setFilteredNodes, roadmapId, updateRoadmapNodesSectionAndTimePeriod]
  );

  useEffect(() => {
    sortedTimePeriodsRef.current = sortedTimePeriods;
  }, [sortedTimePeriods]);
  useEffect(() => {
    sortedVerticalSectionsRef.current = sortedVerticalSections;
  }, [sortedVerticalSections]);
  useEffect(() => {
    sortedHorizontalSectionsRef.current = sortedHorizontalSections;
  }, [sortedHorizontalSections]);
  useEffect(() => {
    filteredNodesRef.current = filteredNodes;
  }, [filteredNodes]);

  useEffect(() => {
    if (shouldRecalculateNodePositions) {
      const sortedTimePeriodWidths = sortedTimePeriods.map(({ tabWidth }) => tabWidth);
      const sortedTimePeriodHeights = sortedTimePeriods.map(({ areaHeight }) => areaHeight);

      const sortedSectionWidths = [...sortedVerticalSections, ...sortedHorizontalSections].map(
        ({ tabWidth }) => tabWidth
      );

      const didAnyTimePeriodWidthChange = sortedTimePeriodWidths.some(
        (tpWidth, index) => tpWidth !== prevTimePeriodsWidthRef.current[index]
      );
      const didAnyTimePeriodHeightChange = sortedTimePeriodHeights.some(
        (tpHeight, index) => tpHeight !== prevTimePeriodsHeightRef.current[index]
      );

      const didAnySectionWidthChange = sortedSectionWidths.some(
        (sWidth, index) => sWidth !== prevSectionsWidthRef.current[index]
      );

      const didWidthsLengthChange =
        prevTimePeriodsWidthRef.current.length !== 0 &&
        sortedTimePeriodWidths.length !== prevTimePeriodsWidthRef.current.length;

      const didHeightsLengthChange =
        prevTimePeriodsHeightRef.current.length !== 0 &&
        sortedTimePeriodHeights.length !== prevTimePeriodsHeightRef.current.length;

      const didSectionsLengthChange =
        prevSectionsWidthRef.current.length !== 0 &&
        sortedSectionWidths.length !== prevSectionsWidthRef.current.length;

      const didTimePeriodWidthChange = didAnyTimePeriodWidthChange || didWidthsLengthChange;

      const didTimePeriodHeightChange = didAnyTimePeriodHeightChange || didHeightsLengthChange;

      const didSectionWidthChange = didAnySectionWidthChange || didSectionsLengthChange;

      if (didTimePeriodWidthChange || didTimePeriodHeightChange || didSectionWidthChange) {
        const nodesToUpdate = detectNodeTimePeriodAndSectionIdChanges();
        updateNodes(nodesToUpdate);

        if (didTimePeriodWidthChange) {
          prevTimePeriodsWidthRef.current = sortedTimePeriodWidths;
        }
        if (didTimePeriodHeightChange) {
          prevTimePeriodsHeightRef.current = sortedTimePeriodHeights;
        }
        if (didSectionWidthChange) {
          prevSectionsWidthRef.current = sortedSectionWidths;
        }
      }
    }
    setShouldRecalculateNodePositions(false);
  }, [
    updateNodes,
    setShouldRecalculateNodePositions,
    detectNodeTimePeriodAndSectionIdChanges,
    sortedTimePeriods,
    shouldRecalculateNodePositions,
    sortedVerticalSections,
    sortedHorizontalSections,
  ]);

  useEffect(() => {
    if (!roadmap?.id && !isLoading) {
      navigate(routes.dashboardTest);
    }

    if (roadmap?.id) {
      setLastSavedTime(new Date(roadmap!.updatedAt!));
    }
  }, [roadmap?.id, navigate, isLoading, roadmap]);

  useEffect(() => {
    if (filters?.length) {
      type FilterWithSortOrder = (typeof filters)[0] & {
        sortOrder?: number;
        sort_order?: number; // Add this
      };

      const ordered = [...filters]
        .sort((a, b) => {
          const aFilter = a as FilterWithSortOrder;
          const bFilter = b as FilterWithSortOrder;
          // Check both property names
          const aSort = aFilter.sortOrder ?? aFilter.sort_order ?? 0;
          const bSort = bFilter.sortOrder ?? bFilter.sort_order ?? 0;
          return aSort - bSort;
        })
        .map(f => {
          const filter = f as FilterWithSortOrder;
          return {
            ...f,
            allNodeOptions: f.nodeOptions,
            // Check both property names
            sortOrder: filter.sortOrder ?? filter.sort_order ?? 0,
            isVisible: f.isVisible ?? true,
          };
        });

      setSortableFilters(ordered);
    }
  }, [filters]);

  if (isLoading) {
    return (
      <LoaderWrapper>
        <StyledLoader />
      </LoaderWrapper>
    );
  }

  const filterNodes = ({
    filter,
    optionColor,
    optionSize,
    optionId,
    multiFilterColor,
  }: {
    filter: components['schemas']['Filter'];
    optionColor: string;
    optionSize: number;
    multiFilterColor?: string;
    optionId?: number;
  }) => {
    if (!filter.nodes) return;

    const optionColorMap = new Map<number, string>();
    const optionSizeMap = new Map<number, number>();

    filter.options.forEach(option => {
      optionColorMap.set(Number(option.id), option.color);
      if (typeof option.size === 'number' && isValidNodeSize(option.size)) {
        optionSizeMap.set(Number(option.id), option.size);
      }
    });

    const nodes = filteredNodes.map(fn => {
      let color = '';
      let size: number | undefined;

      if (filter.selection === 'multiple' || isMultiFilterEnabled) {
        if (filter.nodes) {
          const filteredFilterNodes = filter.nodes.filter(n => n.nodeId === fn.id);
          const isOptionMatching = filteredFilterNodes.some(n => n.optionId === optionId);

          if (filteredFilterNodes.length && isOptionMatching && optionId !== undefined) {
            color = multiFilterColor || '';
          }
        }

        return {
          ...fn,
          shape: filter.shape,
          color,
          ...(size !== undefined && { size }),
        };
      }

      if (filter.nodes) {
        const filteredNode = filter.nodes.find(n => n.nodeId === fn.id);
        const isOptionMatching = filteredNode?.optionId === optionId;

        if (filteredNode && isOptionMatching) {
          color = optionColor;
          size = optionSize;
        } else if (filteredNode) {
          const mappedColor = optionColorMap.get(Number(filteredNode.optionId));
          if (mappedColor) {
            color = transparentize(0.85, mappedColor);
          }

          const mappedSize = optionSizeMap.get(Number(filteredNode.optionId));
          if (mappedSize !== undefined) {
            size = mappedSize;
          }
        }
      }

      return {
        ...fn,
        shape: filter.shape,
        color,
        ...(size !== undefined && { size }),
      };
    });

    if (isMultiFilterEnabled) {
      setFilteredNodes(nodes);
    } else {
      const filteredResult = nodes.filter(fn => fn.color !== '');

      setFilteredNodes(filteredResult);
    }
  };

  const updateLastSavedTime = () => {
    const currentTime = new Date();
    setLastSavedTime(currentTime);
  };

  return (
    <EditorContext.Provider
      value={{
        stageRef,
        canvasAreaRef,
        roadmap: roadmap!,
        filters: filters || [],
        filteredNodes: filteredNodes!,
        isSidebarContentLoading: isSidebarContentLoading!,
        setIsSidebarContentLoading: setIsSidebarContentLoading!,
        setIsDraggingTab,
        filterNodes,
        isDraggingTab,
        setDraggedTabPositionChange,
        draggedTabPositionChange,
        setSelectedNodeId,
        selectedNodeId,
        setSelectedInitiativeId,
        selectedInitiativeId,
        editingInitiativeId,
        setEditingInitiativeId,
        isCreatingNewInitiative,
        setIsCreatingNewInitiative,
        sortedTimePeriods,
        sortedVerticalSections,
        sortedHorizontalSections,
        roadmapNodes,
        setFilteredNodes,
        activeFilterIds,
        setActiveFilterIds,
        roadmapVisible,
        popupVisible,
        budgetPopupVisible,
        budgetDropdownVisible,
        budgetRoadmapVisible,
        dropdownVisible,
        setIsPreview,
        isPreview: Boolean(isPreview),
        optionCheckedIds,
        setOptionCheckedIds,
        isPublicView: Boolean(isPublicView),
        isInitiativeDetailDialogOpen,
        setIsInitiativeDetailDialogOpen,
        expandedSidebarAccordionKey,
        setExpandedSidebarAccordionKey,
        generateUniqueIntTemporaryId,
        temporaryCreatedIds,
        isSwimlaneView,
        setIsSwimlaneView,
        isGanttView,
        setIsGanttView,
        setShowDependencies,
        showDependencies,
        isMultiFilterEnabled,
        setIsMultiFilterEnabled,
        canvasAreaOffset,
        height,
        width,
        setHeight,
        setWidth,
        widthToFitContent,
        heightToFitContent,
        detectNodeTimePeriodAndSectionIdChanges,
        setShouldRecalculateNodePositions,
        isSelectingDependencies,
        setIsSelectingDependencies,
        sortedTimePeriodsRef,
        sortedHorizontalSectionsRef,
        sortedVerticalSectionsRef,
        filteredNodesRef,
        lastSavedTime,
        updateLastSavedTime,
        roadmapPermissions,
        reorderedFilterOptions,
        setReorderedFilterOptions,
        sortableFilters,
        setSortableFilters,
        activeDependencyNodeIds,
        setActiveDependencyNodeIds,
        activeDependencyConnectionIds,
        setActiveDependencyConnectionIds,
        activeDisabledConnectionIds,
        setActiveDisabledConnectionIds,
        viewMode,
        setViewMode,
        budgetViewTab,
        setBudgetViewTab,
        initiativeBudgets,
        setInitiativeBudgets,
        isLoadingBudgets,
        setIsLoadingBudgets,
        allBudgets,
        toggledBudgetNodeIds,
        setToggledBudgetNodeIds,
        toggleBudgetNode,
        hiddenChildNodeIds,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
};

export const useEditorContext = () => {
  const context = useContext(EditorContext);

  if (!context) {
    throw Error('EditorContext hook can be used only inside EditorContextProvider');
  }

  return context;
};

const LoaderWrapper = styled.div`
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLoader = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;
